import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { WeaveLogo } from "@/components/WeaveLogo";
import { FadeUp } from "@/components/Motion";
import { getPaymentStatus } from "@/lib/api";

const TIDYCAL_URL = "https://tidycal.com/the-weave/30-minute-meeting";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (cancelled) return;
        if (res.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        attempts += 1;
        if (attempts < 15) {
          setTimeout(tick, 2000);
        } else {
          setStatus("pending");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="payment-success-page">
      <header className="border-b border-weave-line bg-weave-cream/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center">
          <Link to="/" aria-label="Back to home"><WeaveLogo /></Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <FadeUp className="max-w-xl text-center">
          {status === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 className="animate-spin text-weave-teal mx-auto" size={36} />
              <h1 className="mt-6 text-3xl md:text-4xl font-light text-weave-ink">Confirming your payment…</h1>
              <p className="mt-4 text-weave-slate">One moment while Stripe confirms everything.</p>
            </div>
          )}
          {status === "paid" && (
            <div data-testid="payment-confirmed">
              <CheckCircle2 className="text-weave-teal mx-auto" size={44} />
              <h1 className="mt-6 text-3xl md:text-4xl font-light text-weave-ink">
                Your place is <span className="text-weave-teal">secured</span>.
              </h1>
              <p className="mt-5 text-lg text-weave-slate leading-relaxed">
                Your payment has been received, and your place in the April 2027 cohort is secured.
                A confirmation is on its way to your inbox, and we’ll be in touch personally to welcome you.
              </p>
              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <a
                  href={TIDYCAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="success-consult-link"
                  className="inline-flex items-center gap-2 bg-weave-teal text-white hover:bg-weave-tealdeep transition-colors duration-300 rounded-full px-7 py-3.5 font-medium"
                >
                  Book a welcome conversation <ArrowRight size={16} />
                </a>
                <Link
                  to="/"
                  data-testid="success-home-link"
                  className="inline-flex items-center gap-2 border border-weave-teal text-weave-teal hover:bg-weave-teal/10 transition-colors duration-300 rounded-full px-7 py-3.5 font-medium"
                >
                  Back to The Weave
                </Link>
              </div>
            </div>
          )}
          {status === "pending" && (
            <div data-testid="payment-pending">
              <h1 className="text-3xl md:text-4xl font-light text-weave-ink">Nearly there.</h1>
              <p className="mt-5 text-lg text-weave-slate leading-relaxed">
                Stripe is still confirming your payment — this can take a minute. If you were
                charged, your place is secured and a confirmation email will follow shortly.
              </p>
              <Link to="/" className="mt-8 inline-flex items-center gap-2 text-weave-teal font-medium" data-testid="pending-home-link">
                Back to The Weave <ArrowRight size={16} />
              </Link>
            </div>
          )}
          {status === "error" && (
            <div data-testid="payment-error">
              <h1 className="text-3xl md:text-4xl font-light text-weave-ink">We couldn’t confirm that payment.</h1>
              <p className="mt-5 text-lg text-weave-slate leading-relaxed">
                If you completed checkout, don’t worry — email hello@wearetheweave.co.uk and we’ll
                confirm your place manually.
              </p>
              <Link to="/" className="mt-8 inline-flex items-center gap-2 text-weave-teal font-medium" data-testid="error-home-link">
                Back to The Weave <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </FadeUp>
      </main>
    </div>
  );
}
